/** Display helpers for cards: art paths and plain-English rule text (from `text.json`). */
import { hasText, t, type CardDef, type CardId, type DeckColor } from '@kadice/shared';

export const cardImage = (color: DeckColor | null, card: CardId | 'back' | 'hold') =>
  `/img/cards/${color ?? 'blue'}-${card}.png`;

/** Card art already fetched and decoded; kept so the browser holds on to it. */
const preloaded = new Map<string, HTMLImageElement>();

/** Fetch and decode card art ahead of time, so it shows at once when a card flips or zooms. */
export function preloadCards(urls: Iterable<string>) {
  for (const url of urls) {
    if (preloaded.has(url)) continue;
    const img = new Image();
    img.src = url;
    img.decode().catch(() => {});
    preloaded.set(url, img);
  }
}

/** The roll that destroys a card, e.g. 'a pair of 3s'. */
export const killerName = (card: CardDef) => t(`killer.${card.killer}`);
export const killerText = (card: CardDef) => t('card.destroyedBy', { killer: killerName(card) });

export const powerText = (card: CardDef) => powerTextOf(card.power);

/** A power code in plain English, e.g. 'Move one die up by 1.' */
export function powerTextOf(power: string): string {
  const wild = /^w([1-6])$/.exec(power);
  if (wild) return t('power.wild', { n: wild[1]! });
  return hasText(`power.${power}`) ? t(`power.${power}`) : t('power.none');
}

/** A power code in a few words (Beginner mode's power line on your turn), e.g. 'Move one die up 1.' */
export function shortPowerOf(power: string): string {
  const wild = /^w([1-6])$/.exec(power);
  if (wild) return t('shortPower.wild', { n: wild[1]! });
  return hasText(`shortPower.${power}`) ? t(`shortPower.${power}`) : t('shortPower.none');
}

export const cardTooltip = (card: CardDef | undefined) =>
  card ? `${card.name}\n${killerText(card)}\n${powerText(card)}` : t('card.hidden');
